import axios from 'axios';

const API_BASE = 'https://session-companion-production.up.railway.app';

// Canonical domain for links shown to or sent to clients (portal invite links,
// magic links, "return anytime at..." text). Deliberately NOT window.location.origin
// — a coach browsing the app via the legacy session-companion-beta.vercel.app URL
// would otherwise generate/copy links on that domain instead of the branded one.
export const PORTAL_BASE_URL = 'https://app.transformationcompanion.com';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401, try to refresh token then retry. Concurrent 401s (e.g. several
// requests firing in parallel on page load) share a single in-flight refresh
// instead of each calling /auth/refresh with the same refresh token — Supabase
// refresh tokens are single-use/rotating, so racing requests would otherwise
// each get a stale token and fail, wiping the session and bouncing to /login
// even though the very first refresh actually succeeded.
let refreshPromise = null;

const performRefresh = () => {
  if (!refreshPromise) {
    const refresh = localStorage.getItem('refresh_token');
    refreshPromise = (refresh
      ? axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refresh })
      : Promise.reject(new Error('No refresh token')))
      .then(res => {
        localStorage.setItem('access_token', res.data.access_token);
        localStorage.setItem('refresh_token', res.data.refresh_token);
        return res.data.access_token;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const newToken = await performRefresh();
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (e) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const signup = (data) => api.post('/auth/signup', data);
export const login = (data) => api.post('/auth/login', data);
export const logout = () => api.post('/auth/logout');
export const changePassword = (currentPassword, newPassword) =>
  api.put('/auth/password', { current_password: currentPassword, new_password: newPassword });

// Profile
export const getProfile = () => api.get('/profile');
export const updateProfile = (data) => api.put('/profile', data);
export const getModalities = () => api.get('/modalities');
export const deleteAccount = () => api.delete('/account');

// Clients
export const getClients = () => api.get('/clients');
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);
export const checkClientEmail = (email, excludeClientId) => api.get('/clients/check-email', { params: { email, exclude_client_id: excludeClientId } });

// Sessions
export const getAllSessions = (sessionType) => api.get('/sessions', { params: sessionType ? { session_type: sessionType } : {} });
export const getSessions = (clientId) => api.get(`/clients/${clientId}/sessions`);
export const createSession = (clientId, data) => api.post(`/clients/${clientId}/sessions`, data);
export const updateSession = (sessionId, data) => api.put(`/sessions/${sessionId}`, data);

// AI
export const generateSummary = (data) => api.post('/generate-summary', data);
export const getTransformationArc = (clientId) => api.get(`/clients/${clientId}/transformation`);

// Insights
export const getInsights = () => api.get('/insights');

export default api;

// Feedback
export const submitFeedback = (data) => api.post('/feedback', data);

// Avatar
export const buildAvatar = (clientId) => api.post(`/clients/${clientId}/build-avatar`);
export const updateAvatarSection = (clientId, updates) => api.patch(`/clients/${clientId}/avatar`, updates);

// Portal
export const generateAccessCode = (clientId, code) => api.post(`/clients/${clientId}/generate-access-code`, { code });
export const resetClientPortalPassword = (clientId) => api.post(`/clients/${clientId}/reset-portal-password`);
export const sendClientInvite = (clientId) => api.post(`/clients/${clientId}/send-invite`);
export const generateMagicLink = (clientId) => api.post(`/clients/${clientId}/generate-magic-link`);
export const getMessages = (clientId) => api.get(`/clients/${clientId}/messages`);
export const sendMessage = (clientId, data) => api.post(`/clients/${clientId}/messages`, data);
export const uploadVoiceMessage = (clientId, formData) => api.post(`/clients/${clientId}/upload-voice`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateMessage = (messageId, data) => api.patch(`/messages/${messageId}`, data);
export const analyzeMessages = (clientId) => api.post(`/clients/${clientId}/analyze-messages`);
export const getClientTelegramLink = (clientId) => api.get(`/clients/${clientId}/telegram-link`);
export const disconnectClientTelegram = (clientId) => api.delete(`/clients/${clientId}/telegram`);
export const getGroupTelegramLink = (groupId) => api.get(`/groups/${groupId}/telegram-link`);
export const disconnectGroupTelegram = (groupId) => api.delete(`/groups/${groupId}/telegram`);
export const getGroupMessages = (groupId) => api.get(`/groups/${groupId}/group-messages`);
export const analyzeGroupChat = (groupId) => api.post(`/groups/${groupId}/analyze-chat`);

// Portal (no auth)
export const validateAccessCode = (code) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/validate/${code}`).then(r => r.json());
export const portalLogin = (data) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }).then(r => r.json());
export const portalSetup = (data) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/setup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }).then(r => r.json());
export const submitPortalIntake = (accessCode, data) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/intake/${accessCode}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }).then(r => r.json());
export const getPortalData = (clientId, token) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/${clientId}/data`, {
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(r => r.json());
export const clientSendMessage = (clientId, token, data) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/${clientId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  }).then(r => r.json());
export const getPortalGoals = (clientId, token) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/${clientId}/goals`, {
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(r => r.json());
export const addPortalGoal = (clientId, token, text) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/${clientId}/goals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ text })
  }).then(r => r.json());
export const getPortalBreakthroughs = (clientId, token) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/${clientId}/breakthroughs`, {
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(r => r.json());
export const getPortalProfile = (clientId, token) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/${clientId}/profile`, {
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(r => r.json());
export const updatePortalProfile = (clientId, token, data) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/${clientId}/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(data)
  }).then(r => r.json());
export const updatePortalIntentions = (clientId, token, intentions) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/${clientId}/intentions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ intentions })
  }).then(r => r.json());
export const changePortalPassword = (clientId, token, data) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/${clientId}/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(data)
  }).then(r => r.json());
export const redeemMagicLink = (accessCode, magicToken) =>
  fetch(`${process.env.REACT_APP_API_URL}/portal/magic/${accessCode}/${magicToken}`).then(r => r.json());

export const getGroups = () => api.get('/groups');
export const createGroup = (data) => api.post('/groups', data);
export const getGroup = (groupId) => api.get(`/groups/${groupId}`);
export const updateGroup = (groupId, data) => api.patch(`/groups/${groupId}`, data);
export const deleteGroup = (groupId) => api.delete(`/groups/${groupId}`);
export const updateGroupClients = (groupId, clientIds) => api.post(`/groups/${groupId}/clients`, { client_ids: clientIds });
export const buildGroupModality = (groupId, notes) => api.post(`/groups/${groupId}/build-modality`, { notes: notes || null });
export const getGroupSessions = (groupId) => api.get(`/groups/${groupId}/sessions`);
export const getGroupSessionTypes = (groupId) => api.get(`/groups/${groupId}/session-types`);
export const createGroupSessionType = (groupId, name) => api.post(`/groups/${groupId}/session-types`, { name });
export const deleteGroupSessionType = (groupId, typeId) => api.delete(`/groups/${groupId}/session-types/${typeId}`);
export const getSessionTypes = () => api.get('/session-types');
export const createSessionType = (name) => api.post('/session-types', { name });
export const deleteSessionType = (typeId) => api.delete(`/session-types/${typeId}`);
export const getOnboardingQuestions = () => api.get('/onboarding-questions');
export const saveOnboardingQuestions = (questions) => api.put('/onboarding-questions', { questions });
export const getOfferings = () => api.get('/offerings');
export const createOffering = (data) => api.post('/offerings', data);
export const updateOffering = (offeringId, data) => api.put(`/offerings/${offeringId}`, data);
export const getClientGroups = (clientId) => api.get(`/clients/${clientId}/groups`);
export const setClientOfferings = (clientId, offeringIds) => api.put(`/clients/${clientId}/offerings`, { offering_ids: offeringIds });
export const markMessagesRead = (clientId) => api.patch(`/clients/${clientId}/messages/read`);
export const getUnreadCounts = () => api.get('/unread-counts');
export const getConversations = () => api.get('/messages/conversations');
export const savePushSubscription = (data) => api.post('/push-subscription', data);
export const getScheduledSessions = () => api.get('/scheduled-sessions');
export const createScheduledSession = (data) => api.post('/scheduled-sessions', data);
export const updateScheduledSession = (id, data) => api.patch(`/scheduled-sessions/${id}`, data);
export const deleteScheduledSession = (id) => api.delete(`/scheduled-sessions/${id}`);
export const getClientScheduledSessions = (clientId) => api.get(`/clients/${clientId}/scheduled-sessions`);
export const getTaggedMessages = (clientId, tag) => api.get(`/clients/${clientId}/messages/tagged?tag=${tag}`);

// Transcription
export const transcribeStart = (formData) => api.post('/transcribe/start', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const transcribeStatus = (transcriptId) => api.get(`/transcribe/status/${transcriptId}`);

// Pending recordings (Zoom/Meet auto-import)
export const getPendingRecordings = () => api.get('/pending-recordings');
export const getPendingRecording = (source, externalId) => api.get(`/pending-recordings/${source}/${externalId}`);
export const importPendingRecording = (source, externalId, payload) => api.post(`/pending-recordings/${source}/${externalId}/import`, payload);
export const ignorePendingRecording = (source, externalId) => api.delete(`/pending-recordings/${source}/${externalId}`);

// Client goals (coach-auth)
export const getClientGoals = (clientId, source) => api.get(`/clients/${clientId}/goals${source ? `?source=${source}` : ''}`);
export const createClientGoal = (clientId, data) => api.post(`/clients/${clientId}/goals`, data);
export const updateClientGoal = (clientId, goalId, data) => api.patch(`/clients/${clientId}/goals/${goalId}`, data);
export const deleteClientGoal = (clientId, goalId) => api.delete(`/clients/${clientId}/goals/${goalId}`);

// Session prep
export const generateSessionPrep = (data) => api.post('/sessions/prep', data);

// Marketing
export const getWins = () => api.get('/marketing/wins');
export const getSuggestedWins = () => api.get('/marketing/suggested-wins');
export const createWin = (data) => api.post('/marketing/wins', data);
export const deleteWin = (id) => api.delete(`/marketing/wins/${id}`);
export const generateBragReel = (data) => api.post('/marketing/brag-reel', data);
export const generateBio = (data) => api.post('/marketing/bio', data);
export const getMarketingPage = () => api.get('/marketing/page');
export const generateMarketingPage = (data) => api.post('/marketing/page/generate', data);
export const updateMarketingPage = (data) => api.put('/marketing/page', data);
export const uploadMarketingPhoto = (formData) => api.post('/marketing/page/photos', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteMarketingPhoto = (storagePath) => api.delete('/marketing/page/photos', { data: { storage_path: storagePath } });
export const getPublicMarketingPage = (slug) => api.get(`/site/${slug}`);

// Admin
export const getAdminUsers = () => api.get('/admin/users');
export const getAdminFeedback = () => api.get('/admin/feedback');
export const getAdminWaitlist = () => api.get('/admin/waitlist');
export const sendAdminPasswordReset = (userId) => api.post(`/admin/users/${userId}/send-password-reset`);
export const getAdminUserClients = (userId) => api.get(`/admin/users/${userId}/clients`);
export const getAdminClients = () => api.get('/admin/clients');
export const adminDeleteClient = (clientId) => api.delete(`/admin/clients/${clientId}`);
export const getAdminUsage = () => api.get('/admin/usage');
